import { useTranslation } from 'next-i18next';
import { type ChangeEvent } from 'react';
import { useFormikContext } from 'formik';
import tw from 'twin.macro';
import Checkbox from '@components/elements/Checkbox/Checkbox';
import ConsentCheckbox from '@components/modules/ConsentCheckbox/ConsentCheckbox';
import INPUT_IDS from '@constants/inputs/registerInputs';
import useAppConfig from '@hooks/useAppConfig';

import type { FormValues } from './types/formValues';

type ConsentSplitFieldsProps = {
  lineClamp?: number;
};

const ConsentSplitFields = ({ lineClamp }: ConsentSplitFieldsProps) => {
  const { t } = useTranslation();
  const {
    data: { brandTerms },
  } = useAppConfig();

  const { setFieldValue, values } = useFormikContext<FormValues>();

  const emailConsent = brandTerms.find(term => term.type === 'EMAIL');
  const smsConsent = brandTerms.find(term => term.type === 'SMS');
  const phoneConsent = brandTerms.find(term => term.type === 'PHONE');

  const handleGroupCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue(INPUT_IDS.EMAIL_CONSENT, e.target.checked);
    setFieldValue(INPUT_IDS.SMS_CONSENT, e.target.checked);
    setFieldValue(INPUT_IDS.PHONE_CONSENT, e.target.checked);
  };

  const isGroupChecked =
    values[INPUT_IDS.EMAIL_CONSENT] &&
    values[INPUT_IDS.SMS_CONSENT] &&
    values[INPUT_IDS.PHONE_CONSENT];

  const areAllConsentsActive = !!(
    emailConsent?.active &&
    smsConsent?.active &&
    phoneConsent?.active
  );

  return (
    <div data-cy="ConsentSplitFields_div">
      {areAllConsentsActive && (
        <Checkbox
          data-testid="all-marketing-terms-checkbox"
          cssExtend={{
            wrapper: tw`mb-4`,
          }}
          label={t('$*input.allMarketingTerms.label')}
          checked={isGroupChecked}
          onChange={handleGroupCheckbox}
        />
      )}
      <div css={[tw`grid gap-y-4`, areAllConsentsActive && tw`ml-8`]}>
        {emailConsent?.active && (
          <ConsentCheckbox
            dataTestid="email-consent-checkbox"
            name={INPUT_IDS.EMAIL_CONSENT}
            term={emailConsent}
            bonus={emailConsent.moneyboxBonus}
            lineClamp={lineClamp}
          />
        )}
        {smsConsent?.active && (
          <ConsentCheckbox
            dataTestid="sms-consent-checkbox"
            name={INPUT_IDS.SMS_CONSENT}
            term={smsConsent}
            bonus={smsConsent.moneyboxBonus}
            lineClamp={lineClamp}
          />
        )}
        {phoneConsent?.active && (
          <ConsentCheckbox
            dataTestid="phone-consent-checkbox"
            name={INPUT_IDS.PHONE_CONSENT}
            term={phoneConsent}
            bonus={phoneConsent.moneyboxBonus}
            lineClamp={lineClamp}
          />
        )}
      </div>
    </div>
  );
};

export default ConsentSplitFields;
